import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ChevronDown, ChevronUp, Loader2, Plus, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { useStartStatement, useStatements } from '../hooks';
import { getAccountName, RECONCILABLE_ACCOUNTS } from '../accounts';
import type { StatementReconciliation } from '../types';

/**
 * A statement "needs attention" when the audit surfaced any discrepancy
 * (missing / wrong-amount / duplicate matches), OR when it is mid-audit but
 * has not matched a single line yet (a stalled or empty run).
 */
function needsAttention(s: StatementReconciliation): boolean {
  const discrepancies =
    Number(s.missing_count ?? 0) + Number(s.found_wrong_count ?? 0) + Number(s.duplicate_count ?? 0);
  if (discrepancies > 0) return true;
  return (
    s.status === 'in_progress' &&
    Number(s.found_count ?? 0) === 0 &&
    Number(s.line_count ?? 0) > 0
  );
}

/** Human-readable status labels (raw enum values read as code, not UI). */
const STATUS_LABELS: Record<StatementReconciliation['status'], string> = {
  draft: 'Draft',
  in_progress: 'In progress',
  completed: 'Completed',
  abandoned: 'Abandoned',
};

/**
 * A draft can only be re-audited once it has the inputs the audit needs:
 * an account, a period, and a linked source document.
 */
function canReRunAudit(s: StatementReconciliation): boolean {
  if (s.status !== 'draft') return true;
  return Boolean(s.qb_account_id && s.period_start && s.period_end && s.dms_document_id);
}

/**
 * Compact reconciliation progress: found / total with colored discrepancy
 * badges. Badges only render when their count is non-zero so clean rows stay
 * uncluttered.
 */
function ReconcileProgress({ s }: { s: StatementReconciliation }) {
  // Counts arrive from PG aggregates — coerce defensively so sums never concatenate.
  const total = Number(s.line_count ?? 0);
  const found = Number(s.found_count ?? 0);
  const missing = Number(s.missing_count ?? 0);
  const wrong = Number(s.found_wrong_count ?? 0);
  const dup = Number(s.duplicate_count ?? 0);
  // Lines the audit could not classify (not audited yet, or ambiguous matches).
  const unaudited = Math.max(0, total - found - missing - wrong - dup);

  if (total === 0) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }

  return (
    <div className="flex items-center gap-1.5">
      <span className="font-mono text-xs tabular-nums text-foreground">
        <span className="text-green-700">{found}</span>
        <span className="text-muted-foreground"> / {total}</span>
      </span>
      <div className="flex items-center gap-1">
        {missing > 0 && (
          <span
            className="inline-flex items-center rounded border border-red-200 bg-red-50 px-1.5 text-[11px] font-medium text-red-700"
            title={`${missing} missing`}
          >
            – {missing}
          </span>
        )}
        {wrong > 0 && (
          <span
            className="inline-flex items-center rounded border border-amber-200 bg-amber-50 px-1.5 text-[11px] font-medium text-amber-700"
            title={`${wrong} wrong amount`}
          >
            ≠ {wrong}
          </span>
        )}
        {dup > 0 && (
          <span
            className="inline-flex items-center rounded border border-purple-200 bg-purple-50 px-1.5 text-[11px] font-medium text-purple-700"
            title={`${dup} duplicate`}
          >
            ⧉ {dup}
          </span>
        )}
        {unaudited > 0 && (
          <span
            className="inline-flex items-center rounded border border-slate-200 bg-slate-50 px-1.5 text-[11px] font-medium text-slate-600"
            title={`${unaudited} not audited or ambiguous`}
          >
            ? {unaudited}
          </span>
        )}
      </div>
    </div>
  );
}

export default function StatementsPage() {
  const navigate = useNavigate();
  const { data, isLoading } = useStatements({ pageNumber: 0, pageLength: 50 });
  const startStatement = useStartStatement();

  // Client-side filters
  const [filterAccount, setFilterAccount] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterFrom, setFilterFrom] = useState<string>('');
  const [filterTo, setFilterTo] = useState<string>('');
  const [onlyNeedsAttention, setOnlyNeedsAttention] = useState(false);
  const [sortFromDir, setSortFromDir] = useState<'asc' | 'desc'>('desc');

  const filteredStatements = useMemo(() => {
    const list = data?.statements ?? [];
    return list.filter((s) => {
      if (filterAccount !== 'all' && s.qb_account_id !== filterAccount) return false;
      if (filterStatus !== 'all' && s.status !== filterStatus) return false;
      if (filterFrom && (!s.period_end || s.period_end < filterFrom)) return false;
      if (filterTo && (!s.period_start || s.period_start > filterTo)) return false;
      if (onlyNeedsAttention && !needsAttention(s)) return false;
      return true;
    });
  }, [data, filterAccount, filterStatus, filterFrom, filterTo, onlyNeedsAttention]);

  // Sort by period start ("From"); toggled from the column header.
  const sortedStatements = useMemo(() => {
    const dir = sortFromDir === 'asc' ? 1 : -1;
    return [...filteredStatements].sort(
      (a, b) => dir * (a.period_start ?? '').localeCompare(b.period_start ?? ''),
    );
  }, [filteredStatements, sortFromDir]);

  const attentionCount = useMemo(
    () => (data?.statements ?? []).filter(needsAttention).length,
    [data],
  );

  function handleRowClick(id: string) {
    navigate(`/acc/statements/${id}`);
  }

  // Aggregate counts across all (filtered) reconciliations for a header summary.
  // Number() everywhere: PG bigint aggregates can arrive as strings, and += on a
  // string concatenates instead of summing.
  const summary = useMemo(() => {
    const t = { count: filteredStatements.length, lines: 0, found: 0, missing: 0, wrong: 0, dup: 0 };
    for (const s of filteredStatements) {
      t.lines += Number(s.line_count ?? 0);
      t.found += Number(s.found_count ?? 0);
      t.missing += Number(s.missing_count ?? 0);
      t.wrong += Number(s.found_wrong_count ?? 0);
      t.dup += Number(s.duplicate_count ?? 0);
    }
    return t;
  }, [filteredStatements]);

  return (
    <div className="space-y-4">
      {/* Tab bar + toolbar (compact single row, matching Rocks/KPI page) */}
      <div className="flex items-center justify-between gap-2 border-b border-border flex-wrap">
        <div className="flex items-center gap-0">
          <button
            className="px-4 py-2 text-sm font-medium border-b-2 -mb-px border-primary text-primary"
          >
            Statements
          </button>
        </div>
        <div className="flex items-center gap-3 flex-wrap pb-1.5 pr-1">
          {/* Inline summary chips */}
          <div className="flex items-center gap-1.5 text-xs">
            <span className="rounded-md border bg-muted/30 px-2 py-0.5">
              <span className="text-muted-foreground">{summary.count}</span> stmts
            </span>
            <span className="rounded-md border bg-muted/30 px-2 py-0.5">
              <span className="text-muted-foreground">{summary.lines}</span> lines
            </span>
            <span className="rounded-md border bg-green-50 text-green-900 border-green-200 px-2 py-0.5">
              ✓ {summary.found}
            </span>
            <span className="rounded-md border bg-red-50 text-red-900 border-red-200 px-2 py-0.5">
              – {summary.missing}
            </span>
            <span className="rounded-md border bg-amber-50 text-amber-900 border-amber-200 px-2 py-0.5">
              ≠ {summary.wrong}
            </span>
            <span className="rounded-md border bg-orange-50 text-orange-900 border-orange-200 px-2 py-0.5">
              ⧉ {summary.dup}
            </span>
          </div>
          <Button
            size="sm"
            variant={onlyNeedsAttention ? 'default' : 'outline'}
            aria-pressed={onlyNeedsAttention}
            onClick={() => setOnlyNeedsAttention((v) => !v)}
            title="Show only statements with discrepancies or stalled audits"
          >
            <AlertTriangle className="h-4 w-4 mr-1" />
            Needs attention
            <span
              className={cn(
                'ml-1.5 rounded px-1 text-xs',
                onlyNeedsAttention ? 'bg-white/20' : 'bg-muted',
              )}
            >
              {attentionCount}
            </span>
          </Button>
          <Button
            size="sm"
            onClick={() => navigate('/acc/statements/new')}
          >
            <Plus className="h-4 w-4 mr-1" />
            New Statement
          </Button>
        </div>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="align-top">
                <button
                  type="button"
                  onClick={() => setSortFromDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
                  className="font-medium pb-1 flex items-center gap-1 hover:text-foreground"
                  aria-label={`Sort by From date, ${sortFromDir === 'asc' ? 'ascending' : 'descending'}`}
                >
                  From
                  {sortFromDir === 'asc' ? (
                    <ChevronUp className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronDown className="h-3.5 w-3.5" />
                  )}
                </button>
                <Input
                  type="date"
                  value={filterFrom}
                  onChange={(e) => setFilterFrom(e.target.value)}
                  className="h-7 text-xs"
                />
              </TableHead>
              <TableHead className="align-top">
                <div className="font-medium pb-1">To</div>
                <Input
                  type="date"
                  value={filterTo}
                  onChange={(e) => setFilterTo(e.target.value)}
                  className="h-7 text-xs"
                />
              </TableHead>
              <TableHead className="align-top min-w-[220px]">
                <div className="font-medium pb-1">QB Account</div>
                <Select value={filterAccount} onValueChange={setFilterAccount}>
                  <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All accounts</SelectItem>
                    <SelectGroup>
                      <SelectLabel>Bank accounts</SelectLabel>
                      {RECONCILABLE_ACCOUNTS.filter((a) => a.kind === 'bank').map((a) => (
                        <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                      ))}
                    </SelectGroup>
                    <SelectGroup>
                      <SelectLabel>Credit cards</SelectLabel>
                      {RECONCILABLE_ACCOUNTS.filter((a) => a.kind === 'credit_card').map((a) => (
                        <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </TableHead>
              <TableHead className="align-top min-w-[140px]">
                <div className="font-medium pb-1">Status</div>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="in_progress">In progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="abandoned">Abandoned</SelectItem>
                  </SelectContent>
                </Select>
              </TableHead>
              <TableHead className="align-top min-w-[220px]">Reconciliation</TableHead>
              <TableHead className="align-top">Created</TableHead>
              <TableHead className="text-right align-top">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                  Loading…
                </TableCell>
              </TableRow>
            ) : sortedStatements.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                  {data?.statements.length
                    ? 'No reconciliations match the filters.'
                    : 'No reconciliations yet. Click Create to start one.'}
                </TableCell>
              </TableRow>
            ) : (
              sortedStatements.map((s) => {
                const attention = needsAttention(s);
                const reAuditable = canReRunAudit(s);
                const isReRunning = startStatement.isPending && startStatement.variables === s.id;
                return (
                  <TableRow
                    key={s.id}
                    onClick={() => handleRowClick(s.id)}
                    className={cn('cursor-pointer', attention && 'bg-red-50/40 hover:bg-red-50/70')}
                  >
                    <TableCell className="font-mono text-xs">{s.period_start?.slice(0, 10) ?? '—'}</TableCell>
                    <TableCell className="font-mono text-xs">{s.period_end?.slice(0, 10) ?? '—'}</TableCell>
                    <TableCell>{getAccountName(s.qb_account_id)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <Badge
                          variant={s.status === 'completed' ? 'default' : 'secondary'}
                          className={cn(
                            s.status === 'in_progress' && 'bg-blue-50 text-blue-800 border border-blue-200 hover:bg-blue-50',
                            s.status === 'abandoned' && 'bg-muted text-muted-foreground',
                          )}
                        >
                          {STATUS_LABELS[s.status] ?? s.status}
                        </Badge>
                        {attention && (
                          <AlertTriangle
                            className="h-3.5 w-3.5 text-red-600"
                            aria-label="Needs attention"
                          />
                        )}
                      </div>
                    </TableCell>
                    <TableCell><ReconcileProgress s={s} /></TableCell>
                    <TableCell className="font-mono text-xs">{s.created_at?.slice(0, 10) ?? '—'}</TableCell>
                    <TableCell className="text-right">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            {/* span wrapper keeps the tooltip working while the button is disabled */}
                            <span
                              className="inline-flex"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                aria-label="Re-run audit"
                                disabled={!reAuditable || isReRunning}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  startStatement.mutate(s.id);
                                }}
                              >
                                {isReRunning ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <RefreshCw className="h-4 w-4" />
                                )}
                              </Button>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            {reAuditable
                              ? 'Re-run QB audit'
                              : 'Draft needs an account, period, and document before auditing'}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
